import { useCallback, useEffect, useRef, useState } from 'react'
import { useFlowReferenceData } from '@forwardflow/features/campaign-flow/hooks/useFlowReferenceData'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FlowShell } from '@forwardflow/features/campaign-flow/components/frame/FlowShell'
import {
   Panel,
   Rail,
   TopBar,
} from '@forwardflow/features/campaign-flow/components/frame/FlowFrame'
import { LaunchPhone } from '@forwardflow/features/campaign-flow/components/phone/LaunchPhone'
import { PlaybookEditor } from '@forwardflow/features/campaign-flow/components/playbook/PlaybookEditor'
import {
   HandoffStep,
   LaunchingStep,
   LaunchStep,
   NumbersStep,
   VoiceStep,
} from '@forwardflow/features/campaign-flow/components/steps/SetupSteps'
import { WhoStep } from '@forwardflow/features/campaign-flow/components/steps/WhoStep'
import {
   Pill,
   Spinner,
} from '@forwardflow/features/campaign-flow/components/ui/primitives'
import { useFlowBase } from '@forwardflow/features/campaign-flow/hooks/useFlowBase'
import {
   IFlowDraft,
   IPhoneNumberOption,
   TEditTab,
   TFlowStep,
   TRailKey,
   ITeammate,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { campaignFlowService } from '@forwardflow/features/campaign-flow/services'
import {
   ASKED,
   FLOW_STEPS,
   RAIL_OF,
   useCampaignFlowStore,
} from '@forwardflow/features/campaign-flow/store/flow.store'
import {
   changedTypes,
   contentOf,
   counts,
   markMine,
   startTiming,
   withTypes,
} from '@forwardflow/features/campaign-flow/utils/playbook'
import { audienceTypes } from '@forwardflow/features/campaign-flow/utils/describe'
import {
   numberForCode,
   suggestedCodes,
} from '@forwardflow/features/campaign-flow/utils/numbers'
import { errorText } from '@forwardflow/features/campaign-flow/utils/errors'
import { plural } from '@forwardflow/features/campaign-flow/utils/text'
import '@forwardflow/features/campaign-flow/components/campaign-flow.css'

const HANDOFF_SHORT = {
   round_robin: 'Team, in turns',
   first_to_answer: 'Team, first to pick up',
} as const

const railValues = (d: IFlowDraft): Partial<Record<TRailKey, string>> => {
   const c = counts(d.playbook.touches)
   return {
      who:
         d.audience.leadCount !== null
            ? `${(d.audience.leadCount ?? 0).toLocaleString()} leads · ${d.audience.name}`
            : undefined,
      handoff: HANDOFF_SHORT[d.handoff.mode],
      voice: d.voice.name,
      playbook: `${audienceTypes(d.audience).some((t) => contentOf(d.playbook, t).origin === 'mine') ? 'Yours · ' : ''}${audienceTypes(d.audience).length > 1 ? `${audienceTypes(d.audience).length} scripts · ` : ''}${plural(c.calls, 'call')}, ${plural(c.texts, 'text')}`,
      numbers: d.numbers.selected.length
         ? d.numbers.selected.map((n) => `(${n.areaCode})`).join(' ')
         : 'Picked from your leads',
   }
}

/**
 * Guided campaign creation, front end only. Three questions (who, hand-off,
 * voice), then launch. Playbook and numbers are ready from the start and open
 * from the rail only if the user wants to change them. Every Continue saves
 * the draft through `campaignFlowService` (live backend when signed in, demo
 * in the preview). Deep links:
 * `?step=voice`, `?step=playbook&tab=texts`.
 */
export const CampaignFlowPage = () => {
   useFlowReferenceData()
   const navigate = useNavigate()
   const { base } = useFlowBase()
   const [params, setParams] = useSearchParams()
   const store = useCampaignFlowStore()
   const {
      draft,
      step,
      done,
      markDone,
      setStep,
      update,
      setSaving,
      saving,
      openSide,
      closeSide,
   } = store
   const [busy, setBusy] = useState(false)
   const [error, setError] = useState<string | null>(null)
   const started = useRef(false)
   /*
    * The team. With one person there is nobody to choose between, so the
    * hand-off question is answered for them (that person, in turns) and the
    * flow goes from Who straight to Voice.
    */
   const [team, setTeam] = useState<ITeammate[] | null>(null)
   useEffect(() => {
      void campaignFlowService
         .teammates()
         .catch(() => [] as ITeammate[])
         .then(setTeam)
   }, [])
   const solo = team?.length === 1 ? team[0] : null

   /* Open the screen named in the URL ("New campaign" resets the draft before navigating here). */
   useEffect(() => {
      if (started.current) return
      started.current = true
      const fromUrl = params.get('step') as TFlowStep | null
      if (fromUrl === 'playbook' || fromUrl === 'numbers')
         openSide(fromUrl, (params.get('tab') as TEditTab) || undefined)
      else if (fromUrl && FLOW_STEPS.includes(fromUrl)) setStep(fromUrl)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   /* Keep the URL in step so a reload or a shared link opens the same screen. */
   useEffect(() => {
      const next = new URLSearchParams()
      next.set('step', step)
      if (step === 'playbook') next.set('tab', store.editTab)
      if (next.toString() !== params.toString())
         setParams(next, { replace: true })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [step, store.editTab])

   const save = useCallback(async () => {
      setSaving(true)
      try {
         const { id } = await campaignFlowService.saveDraft(
            useCampaignFlowStore.getState().draft
         )
         setSaving(false, id)
      } catch (e) {
         setSaving(false)
         throw e
      }
   }, [setSaving])

   /** Run a step's backend work; on failure keep everything typed and say what went wrong. */
   const run = async (work: () => Promise<void>) => {
      setBusy(true)
      setError(null)
      try {
         await work()
      } catch (e) {
         setError(errorText(e))
      } finally {
         setBusy(false)
      }
   }

   /** Numbers are ready without asking: one in each area code most leads live in, owned ones first. */
   const ensureNumbers = async (): Promise<boolean> => {
      if (useCampaignFlowStore.getState().draft.numbers.selected.length)
         return false
      const [codes, owned] = await Promise.all([
         campaignFlowService
            .areaCodes(useCampaignFlowStore.getState().draft.audience)
            .then(suggestedCodes)
            .catch(() => []),
         campaignFlowService.ownedNumbers().catch(() => []),
      ])
      /* A spare number the organization already owns in that area code, else a new one there. */
      const picks: IPhoneNumberOption[] = []
      for (const c of codes) {
         const n =
            numberForCode(c.code, owned, picks) ??
            (
               await campaignFlowService
                  .availableNumbers(c.code)
                  .catch(() => [] as IPhoneNumberOption[])
            ).find((x) => x.areaCode === c.code)
         if (n) picks.push(n)
      }
      update((d) => ({
         ...d,
         numbers: { areaCodes: codes.map((c) => c.code), selected: picks },
      }))
      return picks.length > 0
   }

   const answer = (key: TRailKey, to: TFlowStep) =>
      run(async () => {
         if (key === 'who' && solo) {
            update((d) => ({
               ...d,
               handoff: { mode: 'round_robin', agentIds: [solo.id] },
            }))
            markDone('handoff')
         }
         if (key === 'who') {
            /* Words for each chosen lead type come from the library, unless the user already changed them. */
            const s = useCampaignFlowStore.getState()
            const types = audienceTypes(s.draft.audience)
            const current = withTypes(s.draft.playbook, types)
            const stale = types.filter(
               (t) =>
                  !s.draft.playbook.byType?.[t] ||
                  contentOf(current, t).origin === 'recommended'
            )
            if (stale.length) {
               const rec = await campaignFlowService.recommendedPlaybook(
                  s.draft.audience
               )
               const mine = types.some(
                  (t) => contentOf(current, t).origin === 'mine'
               )
               const byType = { ...current.byType }
               stale.forEach((t) => {
                  byType[t] = contentOf(rec, t)
               })
               /* Timing is shared: keep the user's if they already shaped any words. */
               const base = mine
                  ? current
                  : {
                       ...current,
                       preset: rec.preset,
                       touches: rec.touches,
                       usedByTeams: rec.usedByTeams,
                    }
               update((d) => ({
                  ...d,
                  playbook: startTiming(
                     withTypes({ ...base, byType }, types),
                     d.audience.kinds[0]
                  ),
               }))
            } else
               update((d) => ({
                  ...d,
                  playbook: startTiming(current, d.audience.kinds[0]),
               }))
            /*
             * The numbers first (usually ready: the Who step counts where the
             * leads live in the background), then one save with them in it.
             * Side by side, the counting's requests held the save back.
             */
            await ensureNumbers()
            await save()
         } else await save()
         markDone(key)
         /* The voice is all a campaign made before the flow can change; back to its page. */
         const d = useCampaignFlowStore.getState().draft
         if (d.legacy && d.id) return navigate(`${base}/${d.id}`)
         /* The hand-off question may just have been answered for a one-person team. */
         setStep(
            key === 'who' && solo && to === 'handoff' ? nextAfter('who') : to
         )
      })
   const nextAfter = (s: TFlowStep): TFlowStep => {
      const d = useCampaignFlowStore.getState().done
      return (
         ASKED.find(
            (x, i) =>
               i > ASKED.indexOf(s) &&
               (x === 'launch' || !d.includes(RAIL_OF[x]))
         ) ?? 'launch'
      )
   }

   const goRail = (key: TRailKey) => {
      if (key === 'playbook' || key === 'numbers') return openSide(key)
      setStep(key as TFlowStep)
   }
   const finishSide = async () => {
      const s = useCampaignFlowStore.getState()
      if (s.step === 'playbook' && s.playbookBefore) {
         const types = audienceTypes(s.draft.audience)
         const edited = changedTypes(s.playbookBefore, s.draft.playbook, types)
         if (edited.length)
            update((d) => ({
               ...d,
               playbook: markMine(d.playbook, edited, types),
            }))
      }
      await run(async () => {
         await save()
         closeSide()
      })
   }
   /** A question can be opened once it is answered, or when it is the next one. */
   const firstOpen =
      ASKED.find((x) => x !== 'launch' && !done.includes(RAIL_OF[x])) ??
      'launch'
   /*
    * A campaign made before the flow: only its voice can be changed here.
    * Otherwise the playbook and the numbers are ready from the start and
    * always open; a question opens once answered or when it is next.
    */
   const locked = (key: TRailKey) =>
      draft.legacy
         ? key !== 'voice'
         : key === 'playbook' || key === 'numbers'
           ? false
           : !done.includes(key) && RAIL_OF[firstOpen] !== key

   const body = (() => {
      switch (step) {
         case 'who':
            return (
               <WhoStep
                  busy={busy}
                  onDone={() => void answer('who', nextAfter('who'))}
               />
            )
         case 'handoff':
            return (
               <HandoffStep
                  busy={busy}
                  onBack={() => setStep('who')}
                  onDone={() => void answer('handoff', nextAfter('handoff'))}
               />
            )
         case 'voice':
            return (
               <VoiceStep
                  busy={busy}
                  onBack={() =>
                     draft.legacy && draft.id
                        ? navigate(`${base}/${draft.id}`)
                        : setStep(solo ? 'who' : 'handoff')
                  }
                  onDone={() => void answer('voice', 'launch')}
               />
            )
         case 'playbook':
            return (
               <PlaybookEditor
                  busy={busy}
                  onDone={() => void finishSide()}
                  onBack={() => {
                     useCampaignFlowStore.getState().discardEdits()
                     closeSide()
                  }}
               />
            )
         case 'numbers':
            return <NumbersStep busy={busy} onDone={() => void finishSide()} />
         case 'launch':
            return (
               <>
                  <LaunchStep
                     busy={busy}
                     onBack={() => setStep('voice')}
                     onGo={goRail}
                     onTest={() => store.setPhoneOpen(true)}
                     onSaveDraft={() =>
                        void run(async () => {
                           await save()
                           /* The drafts tab keeps it; launching can wait. */
                           navigate(base)
                        })
                     }
                     onLaunch={() =>
                        void run(async () => {
                           await save()
                           const d = useCampaignFlowStore.getState().draft
                           /* A running campaign keeps running; its page shows the saved setup. */
                           if (d.status === 'active' && d.id)
                              return navigate(`${base}/${d.id}`)
                           await campaignFlowService.activate(d)
                           update((x) => ({ ...x, status: 'active' }))
                           setStep('launching')
                        })
                     }
                  />
                  <LaunchPhone
                     open={store.phoneOpen}
                     onClose={() => store.setPhoneOpen(false)}
                  />
               </>
            )
         case 'launching':
            return (
               <LaunchingStep
                  leads={draft.audience.leadCount ?? 0}
                  onDone={() => {
                     const d = useCampaignFlowStore.getState().draft
                     /* Back to the campaign dashboard, which shows the new campaign at the top. */
                     navigate(base, {
                        state: {
                           launched: { id: d.id, name: d.audience.name },
                        },
                     })
                  }}
               />
            )
      }
   })()

   return (
      <FlowShell className="!overflow-y-auto">
         <div
            className={`cf flex flex-col lg:h-full lg:min-h-[640px] min-h-0 gap-5 transition-[margin] duration-300 ${store.phoneOpen && (step === 'playbook' || step === 'launch') ? 'lg:mr-[360px]' : ''}`}
            data-testid="campaign-flow"
         >
            <TopBar
               name={draft.audience.name || 'New campaign'}
               right={
                  <>
                     <Pill testId="cf-status">
                        {draft.status === 'active' ? 'Live' : 'Draft'}
                     </Pill>
                     {draft.id && (
                        <Pill testId="cf-saved">
                           {saving ? (
                              <>
                                 <Spinner size={10} /> Saving
                              </>
                           ) : (
                              'Saved'
                           )}
                        </Pill>
                     )}
                  </>
               }
            />
            {error && (
               <div
                  className="flex items-start gap-3 rounded-[14px] border border-[#F37370]/35 bg-[#F37370]/[0.08] px-4 py-3 text-sm"
                  role="alert"
                  data-testid="cf-error"
               >
                  <span className="flex-1 min-w-0">{error}</span>
                  <button
                     type="button"
                     onClick={() => setError(null)}
                     aria-label="Dismiss"
                     className="text-white/50 hover:text-white"
                  >
                     ✕
                  </button>
               </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-5 lg:gap-6 flex-1 min-h-0">
               <Rail
                  current={RAIL_OF[step]}
                  done={done}
                  values={railValues(draft)}
                  onGo={goRail}
                  locked={locked}
               />
               <Panel testId={`cf-step-${step}`}>{body}</Panel>
            </div>
         </div>
      </FlowShell>
   )
}
